import { DataType, TypedValue } from "@sourceacademy/conductor/types";
import { ExprNS, StmtNS } from "../../ast-types";
import { TokenType } from "../../tokenizer";
import { Context } from "./context";
import { Environment } from "./environment";
import { Value } from "./stash";

export type Node = { isEnvDependent?: boolean; hasBreakpoint?: boolean } & (
  | StmtNS.Stmt
  | ExprNS.Expr
  | StatementSequence
);

export interface StatementSequence {
  kind: "StatementSequence";
  body: StmtNS.Stmt[];
  loc?: {
    start: { line: number; column: number };
    end: { line: number; column: number };
  };
}

export type ModuleFunctionGenerator = AsyncGenerator<void, TypedValue<DataType>, undefined>;
export type ModuleFunction = (
  args: Value[],
  code: string,
  command: ExprNS.Call,
  context: Context,
) => ModuleFunctionGenerator;

export enum InstrType {
  WHILE = "WhileInstr",
  FOR = "ForInstr",
  ASSIGNMENT = "Assignment",
  LIST_ASSIGNMENT = "ListAssignment",
  APPLICATION = "Application",
  UNARY_OP = "UnaryOperation",
  BINARY_OP = "BinaryOperation",
  BOOL_OP = "BoolOperation",
  BREAK = "BreakInstr",
  CONTINUE = "ContinueInstr",
  LIST = "ListLiteral",
  BRANCH = "Branch",
  POP = "Pop",
  ENVIRONMENT = "environment",
  CONTINUE_MARKER = "continueMarker",
  END_OF_FUNCTION_BODY = "EndOfFunctionBody",
  LIST_ACCESS = "ListAccess",
  MODULE_FUNCTION_CALL = "ModuleFunctionCall",
}

interface BaseInstr {
  instrType: InstrType;
  srcNode: Node;
  isEnvDependent?: boolean;
}

export interface WhileInstr extends BaseInstr {
  instrType: InstrType.WHILE;
  test: ExprNS.Expr;
  body: StatementSequence;
}

export interface ForInstr extends BaseInstr {
  instrType: InstrType.FOR;
  body: StmtNS.Stmt[];
}

export interface ContinueInstr extends BaseInstr {
  instrType: InstrType.CONTINUE;
}

export interface BreakInstr extends BaseInstr {
  instrType: InstrType.BREAK;
}

export interface AssmtInstr extends BaseInstr {
  instrType: InstrType.ASSIGNMENT;
  symbol: string;
  constant: boolean;
  declaration: boolean;
}

export interface UnOpInstr extends BaseInstr {
  instrType: InstrType.UNARY_OP;
  symbol: TokenType;
}

export interface BinOpInstr extends BaseInstr {
  instrType: InstrType.BINARY_OP;
  symbol: TokenType;
}

export interface AppInstr extends BaseInstr {
  instrType: InstrType.APPLICATION;
  numOfArgs: number;
  spreadIndices: number[];
  srcNode: ExprNS.Call;
}

export interface BranchInstr extends BaseInstr {
  instrType: InstrType.BRANCH;
  consequent: Node;
  alternate: Node | null | undefined;
}

export interface ListInstr extends BaseInstr {
  instrType: InstrType.LIST;
  numOfElements: number;
}

export interface ListAccessInstr extends BaseInstr {
  instrType: InstrType.LIST_ACCESS;
}

export interface ListAssmtInstr extends BaseInstr {
  instrType: InstrType.LIST_ASSIGNMENT;
}

export interface EnvInstr extends BaseInstr {
  instrType: InstrType.ENVIRONMENT;
  env: Environment;
}

export interface EndOfFunctionBodyInstr extends BaseInstr {
  instrType: InstrType.END_OF_FUNCTION_BODY;
}

export interface PopInstr extends BaseInstr {
  instrType: InstrType.POP;
}

export interface BoolOpInstr extends BaseInstr {
  instrType: InstrType.BOOL_OP;
  symbol: TokenType;
}

export interface ContinueMarkerInstr extends BaseInstr {
  instrType: InstrType.CONTINUE_MARKER;
}

export interface ModuleFunctionCallInstr extends BaseInstr {
  instrType: InstrType.MODULE_FUNCTION_CALL;
  generator: ModuleFunctionGenerator;
  srcNode: ExprNS.Call;
}

export type Instr =
  | WhileInstr
  | ForInstr
  | AssmtInstr
  | UnOpInstr
  | BinOpInstr
  | AppInstr
  | BranchInstr
  | EnvInstr
  | EndOfFunctionBodyInstr
  | PopInstr
  | BoolOpInstr
  | ListAccessInstr
  | ListInstr
  | ListAssmtInstr
  | BreakInstr
  | ContinueInstr
  | ContinueMarkerInstr
  | ModuleFunctionCallInstr;

/**
 * Reworks a `typeTranslator`/PVML `getPVMLType()` output into the friendlier,
 * unquoted wording used specifically by the "unsupported operand type(s)"
 * family of TypeError messages (see `UnsupportedOperandTypeError` in both
 * src/errors/errors.ts and src/engines/pvml/errors.ts) — spelled-out words
 * ("integer", "boolean") over CPython's own abbreviations ("int", "bool"),
 * "None" over "NoneType", and "function" over "builtin_function_or_method"
 * (no user-visible distinction between a builtin and a closure here, same
 * reasoning as "closure" itself already collapsing to "function"). Deliberately
 * separate from `typeTranslator` itself, which other consumers (the CSE
 * Machine visualizer's stash-value labels, PyCseMachinePlugin.ts) still need
 * in its original, more CPython-literal form — changing `typeTranslator`'s
 * own output would have silently changed those debugger labels too.
 *
 * `variant` (the SICPy chapter, 1-4) additionally governs "list": a pair and
 * a length-2 list are the exact same runtime value in this dialect (no tag
 * distinguishes "made via pair()" from "made via list-literal syntax"), so
 * the value alone can't say which word is right. The chapter can, though —
 * chapters 1-2 have no list-literal syntax at all (NoListsValidator), so any
 * array-shaped value there can only have come from pair()/linked-list
 * construction, unambiguously. At chapter 3+, where both syntaxes coexist
 * and the ambiguity is real, "list" stays the reasonable default.
 */
export function friendlyTypeName(pythonTypeName: string, variant?: number): string {
  switch (pythonTypeName) {
    case "int":
      return "integer";
    case "bool":
      return "boolean";
    case "str":
      return "string";
    case "NoneType":
      return "None";
    case "builtin_function_or_method":
      return "function";
    case "list":
      return variant !== undefined && variant <= 2 ? "pair" : "list";
    default:
      return pythonTypeName;
  }
}

export function typeTranslator(type: Value["type"] | string): string {
  switch (type) {
    case "bigint":
      return "int";
    case "number":
      return "float";
    case "bool":
      return "bool";
    case "string":
      return "str";
    case "list":
      return "list";
    case "complex":
      return "complex";
    case "none":
      return "NoneType";
    case "closure":
      return "function";
    case "builtin":
      // Matches CPython's type(print).__name__.
      return "builtin_function_or_method";
    default:
      // `type` is now `Value["type"] | string` (see this function's own
      // history: errors.ts's TypeError class calls this with a plain
      // `string`, not always a genuine Value["type"] tag) — an unrecognized
      // input isn't necessarily a bug, it may already be a valid Python type
      // name (or some other string a future caller passes deliberately).
      // Passing it through unchanged, not collapsing it to "unknown", is
      // what keeps that widened parameter type actually safe to call with
      // arbitrary strings.
      return type;
  }
}

export function operatorTranslator(operator: TokenType | string) {
  switch (operator) {
    case TokenType.PLUS:
      return "+";
    case TokenType.MINUS:
      return "-";
    case TokenType.STAR:
      return "*";
    case TokenType.SLASH:
      return "/";
    case TokenType.DOUBLESLASH:
      return "//";
    case TokenType.PERCENT:
      return "%";
    case TokenType.DOUBLESTAR:
      return "**";
    case TokenType.LESS:
      return "<";
    case TokenType.GREATER:
      return ">";
    case TokenType.DOUBLEEQUAL:
      return "==";
    case TokenType.NOTEQUAL:
      return "!=";
    case TokenType.LESSEQUAL:
      return "<=";
    case TokenType.GREATEREQUAL:
      return ">=";
    case TokenType.NOT:
      return "not";
    case TokenType.AND:
      return "and";
    case TokenType.OR:
      return "or";
    case TokenType.IS:
      return "is";
    case TokenType.ISNOT:
      return "is not";
    case TokenType.IN:
      return "in";
    case TokenType.NOTIN:
      return "not in";
    default:
      return String(operator);
  }
}
